import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { RotateCcw, Trash2, Loader2, Music, AlertTriangle, ImageOff } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { api, mediaUrl, kindOf, errMsg } from "@/lib/api";

const TrashThumb = ({ g }) => {
  const [broken, setBroken] = useState(false);
  const url = g.thumb_url || g.output_urls?.[0];
  const kind = g.thumb_kind || kindOf(g);
  if (!url || broken || kind === "audio") {
    const Icon = kind === "audio" ? Music : g.status === "failed" ? AlertTriangle : ImageOff;
    return (
      <div className="w-full h-full flex flex-col items-center justify-center gap-2 bg-[#121214] text-zinc-500" data-testid={`trash-thumb-empty-${g.id}`}>
        <Icon className={`w-6 h-6 ${g.status === "failed" ? "text-red-400/70" : ""}`} />
        <span className="text-[10px] font-mono uppercase tracking-widest">{g.status === "failed" ? "Failed · no output" : kind === "audio" ? "Audio" : "No preview"}</span>
      </div>
    );
  }
  const cls = `w-full h-full object-cover ${!g.output_urls?.length ? "opacity-60" : ""}`;
  return kind === "video"
    ? <video src={`${mediaUrl(url)}#t=0.1`} muted playsInline preload="metadata" onError={() => setBroken(true)} className={cls} data-testid={`trash-thumb-${g.id}`} />
    : <img src={mediaUrl(url)} alt="" loading="lazy" onError={() => setBroken(true)} className={cls} data-testid={`trash-thumb-${g.id}`} />;
};

const daysLeft = (d, keep) => Math.max(0, keep - Math.floor((Date.now() - new Date(d).getTime()) / 86400000));

export default function RecycleBin({ open, onOpenChange, projectId, onChanged }) {
  const [items, setItems] = useState([]);
  const [keep, setKeep] = useState(30);
  const [sel, setSel] = useState([]);
  const [busy, setBusy] = useState(false);
  const [confirmIds, setConfirmIds] = useState(null);
  const load = () => api.get(`/trash?project_id=${projectId}`).then((r) => { setItems(r.data.generations); setKeep(r.data.retention_days); });
  useEffect(() => { if (open) { setSel([]); load(); } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const restore = async (id) => { await api.post(`/generations/${id}/restore`); toast.success("Restored to History"); load(); onChanged(); };
  const purge = (ids) => setConfirmIds(ids);
  const doPurge = async (ids) => {
    setConfirmIds(null);
    setBusy(true);
    try { const { data } = await api.post("/trash/purge", { ids }); toast.success(`${data.deleted} permanently deleted`); setSel([]); load(); onChanged(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0D0D0F] border-zinc-800 text-zinc-100 max-w-4xl w-[92vw] max-h-[85vh] flex flex-col" data-testid="trash-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Recycle Bin</DialogTitle>
          <p className="text-xs text-zinc-500">Deleted items stay here for {keep} days, then are removed permanently.</p>
        </DialogHeader>
        <div className="flex items-center gap-2">
          <button className="ax-chip" onClick={() => setSel(sel.length === items.length ? [] : items.map((g) => g.id))} data-testid="trash-select-all">
            {sel.length === items.length && items.length ? "Clear selection" : "Select all"}
          </button>
          <span className="text-xs text-zinc-500">{sel.length} selected</span>
          <div className="flex-1" />
          <button disabled={!sel.length || busy} onClick={() => purge(sel)} data-testid="trash-purge-selected"
            className="ax-chip !text-red-300 !border-red-500/40 disabled:opacity-40">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}Delete permanently
          </button>
        </div>
        {confirmIds && (
          <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-3 flex items-center gap-3" data-testid="trash-confirm">
            <span className="text-sm text-red-200 flex-1">Permanently delete {confirmIds.length} item(s)? This cannot be undone.</span>
            <button className="ax-chip" onClick={() => setConfirmIds(null)} data-testid="trash-confirm-cancel">Cancel</button>
            <button className="ax-chip !bg-red-500 !text-white !border-red-500" onClick={() => doPurge(confirmIds)} data-testid="trash-confirm-delete">Delete forever</button>
          </div>
        )}
        <div className="flex-1 overflow-y-auto grid grid-cols-2 md:grid-cols-4 auto-rows-max gap-3 pr-1">
          {items.length === 0 && <p className="col-span-full text-center text-sm text-zinc-500 py-16" data-testid="trash-empty">Recycle Bin is empty.</p>}
          {items.map((g) => (
            <div key={g.id} className={`ax-gen-card ${sel.includes(g.id) ? "!border-[#7AF25F]/60" : ""}`} data-testid={`trash-item-${g.id}`}>
              <div className="relative aspect-square bg-black">
                <TrashThumb g={g} />
                <Checkbox checked={sel.includes(g.id)} onCheckedChange={() => toggle(g.id)} data-testid={`trash-check-${g.id}`}
                  className="absolute top-2 left-2 bg-black/70 border-white/40" />
                <span className="absolute top-2 right-2 ax-badge bg-black/70">{daysLeft(g.deleted_at, keep)}d left</span>
              </div>
              <div className="p-2.5 space-y-2">
                <p className="text-xs text-zinc-400 line-clamp-2">{g.prompt || g.model_name}</p>
                <div className="flex gap-1.5">
                  <button onClick={() => restore(g.id)} className="ax-chip h-7 text-[11px]" data-testid={`trash-restore-${g.id}`}><RotateCcw className="w-3 h-3" />Restore</button>
                  <button onClick={() => purge([g.id])} className="ax-chip h-7 text-[11px] !text-red-300" data-testid={`trash-delete-${g.id}`}><Trash2 className="w-3 h-3" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
